import { useMemo, useState } from "react";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/feature/LoadingState";
import { useFeedback } from "@/hooks/admin/useAdminData";
import FeedbackCard from "./components/FeedbackCard";
import RatingSummary from "./components/RatingSummary";
import RequestReviewModal from "./components/RequestReviewModal";

const tabs = [
  { key: "all", label: "All Feedback" },
  { key: "new", label: "New" },
  { key: "flagged", label: "Needs Attention" },
  { key: "replied", label: "Replied" },
];

const ratingFilters = ["All", "5★", "4★", "3★", "2★", "1★"];

export default function FeedbackPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [rating, setRating] = useState("All");
  const [requestOpen, setRequestOpen] = useState(false);
  const [toast, setToast] = useState("");
  const { data: feedbackList, loading, error, refetch } = useFeedback();

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: feedbackList.length };
    feedbackList.forEach((f) => {
      c[f.status] = (c[f.status] || 0) + 1;
    });
    return c;
  }, [feedbackList]);

  const filtered = useMemo(() => {
    let list = feedbackList;
    if (activeTab !== "all") list = list.filter((f) => f.status === activeTab);
    if (rating !== "All") {
      const r = parseInt(rating);
      list = list.filter((f) => f.rating === r);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (f) =>
          f.customer.toLowerCase().includes(q) ||
          f.title.toLowerCase().includes(q) ||
          f.body.toLowerCase().includes(q) ||
          f.projectId.toLowerCase().includes(q),
      );
    }
    return list;
  }, [activeTab, search, rating, feedbackList]);

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-ink-500 mb-2">
              <span>Business</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-ink-900 font-semibold">Feedback</span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 leading-tight">
              Customer Feedback
            </h1>
            <p className="text-sm text-ink-500 mt-1.5 max-w-xl">
              Reviews from every channel in one place. Spot trends, recognize top crew, and reply
              to customers right from this inbox.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border border-ink-200 bg-white text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
              <i className="ri-bar-chart-2-line"></i>
              Insights
            </button>
            <button
              onClick={() => setRequestOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              Request Review
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          <div className="lg:col-span-2 space-y-4 md:space-y-5">
            {/* Filters */}
            <div className="bg-white border border-ink-100 rounded-lg">
              <div className="flex items-center gap-1 px-3 pt-2 border-b border-ink-100 overflow-x-auto scrollbar-thin">
                {tabs.map((t) => {
                  const active = t.key === activeTab;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setActiveTab(t.key)}
                      className={`relative px-3 py-3 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                        active ? "text-ink-900" : "text-ink-500 hover:text-ink-700"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {t.label}
                        <span
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            active ? "bg-ink-900 text-white" : "bg-cream-100 text-ink-600"
                          }`}
                        >
                          {counts[t.key] || 0}
                        </span>
                      </span>
                      {active && (
                        <span className="absolute left-3 right-3 -bottom-px h-0.5 bg-brand-500 rounded-full"></span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3">
                <div className="relative flex-1 min-w-0">
                  <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
                    <i className="ri-search-line text-sm"></i>
                  </span>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search reviews by customer, keyword, or project…"
                    className="w-full text-sm pl-10 pr-3 py-2.5 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
                  />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {ratingFilters.map((r) => (
                    <button
                      key={r}
                      onClick={() => setRating(r)}
                      className={`text-xs font-semibold px-2.5 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                        rating === r
                          ? "bg-ink-900 text-white"
                          : "bg-cream-100 text-ink-600 hover:bg-ink-100"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Feedback list */}
            {loading ? (
              <LoadingState message="Loading reviews…" />
            ) : error ? (
              <div className="bg-white border border-ink-100 rounded-lg py-20 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-50 text-red-400 mx-auto mb-3">
                  <i className="ri-error-warning-line text-2xl"></i>
                </div>
                <div className="font-display text-lg font-bold text-ink-900">Failed to load reviews</div>
                <div className="text-sm text-ink-500 mt-1">{error}</div>
                <button
                  onClick={refetch}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Retry
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-ink-100 rounded-lg py-20 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-cream-100 text-ink-400 mx-auto mb-3">
                  <i className="ri-chat-off-line text-2xl"></i>
                </div>
                <div className="font-display text-lg font-bold text-ink-900">No reviews found</div>
                <div className="text-sm text-ink-500 mt-1">
                  Try adjusting your filters or search query.
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {filtered.map((f) => (
                  <FeedbackCard key={f.id} feedback={f} />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4 md:space-y-5">
            <RatingSummary />
          </div>
        </div>
      </div>

      <RequestReviewModal
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        onSent={(count, channel) => {
          setToast(`Review request sent to ${count} ${count === 1 ? "customer" : "customers"} via ${channel}`);
          setTimeout(() => setToast(""), 3500);
        }}
      />

      <div
        className={`fixed bottom-6 right-6 z-[60] transition-all duration-300 ${
          toast ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="bg-ink-900 text-white text-sm font-semibold px-4 py-3 rounded-md border border-ink-800 flex items-center gap-2 max-w-sm">
          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
            <i className="ri-check-line text-sm"></i>
          </span>
          {toast}
        </div>
      </div>
    </AdminLayout>
  );
}